import { Link } from 'react-router-dom';
import { ChevronRight } from '@/utils/icons';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

interface RollupCardProps {
  icon: React.ElementType;
  title: string;
  linkTo: string;
  primary: string;
  secondary?: string;
  variant?: 'default' | 'good' | 'warning' | 'danger';
  loading?: boolean;
}

const variantBorder: Record<string, string> = {
  default: 'dls-border-l-muted-foreground/30',
  good: 'dls-border-l-emerald-400',
  warning: 'dls-border-l-amber-400',
  danger: 'dls-border-l-red-400',
};

export function RollupCard({
  icon: Icon,
  title,
  linkTo,
  primary,
  secondary,
  variant = 'default',
  loading = false,
}: RollupCardProps) {
  if (loading) {
    return (
      <Card className="dls-border-l-4 dls-border-l-muted-foreground/30">
        <CardContent className="dls-p-4">
          <div className="dls-flex dls-items-center dls-gap-2 dls-mb-2">
            <div className="dls-h-4 dls-w-4 dls-rounded dls-bg-muted dls-animate-pulse" />
            <div className="dls-h-4 dls-w-16 dls-rounded dls-bg-muted dls-animate-pulse" />
          </div>
          <div className="dls-h-5 dls-w-28 dls-rounded dls-bg-muted dls-animate-pulse dls-mb-1" />
          <div className="dls-h-3 dls-w-20 dls-rounded dls-bg-muted dls-animate-pulse" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Link to={linkTo} className="dls-block">
      <Card
        className={cn(
          'dls-border-l-4 hover:dls-shadow-20 dls-transition-shadow dls-cursor-pointer',
          variantBorder[variant]
        )}
      >
        <CardContent className="dls-p-4">
          <div className="dls-flex dls-items-start dls-justify-between">
            <div className="dls-min-w-0">
              <div className="dls-flex dls-items-center dls-gap-1.5 dls-mb-1.5">
                <Icon className="dls-h-4 dls-w-4 dls-text-muted-foreground dls-flex-shrink-0" />
                <span className="dls-text-sm dls-font-medium dls-text-muted-foreground">{title}</span>
              </div>
              <p className="dls-text-lg dls-font-bold dls-leading-tight">{primary}</p>
              <p className="dls-text-xs dls-text-muted-foreground dls-mt-0.5">{secondary || '\u00A0'}</p>
            </div>
            <ChevronRight className="dls-h-4 dls-w-4 dls-text-muted-foreground/50 dls-flex-shrink-0 dls-mt-1" />
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
